// В.2.h-rebalance / fix1: Нейро-хирург Зейн — активка «Союзный юнит +3/0/0
// и Пробитие до конца следующего хода».
//
// fix1: armor +1 и hp +2 удалены — активка превращалась в «убийцу Героя» с
// защитой от ответки (winrate Зейна 70% в playtest). Теперь только +3 atk.
// Pierce и длительность оставлены.
//
// Picker: свой юнит (не Герой). Если своих нет — активка не срабатывает.
//
// Эффект (expiresAtTurn = state.currentTurn + 2 — как Адреналин В.2.c):
//   - applyTemporaryBuff +3 atk. Откатывается в Ф1 хода expiresAtTurn
//     утилитой expireTemporaryBuffs.
//   - addPermanentKeyword 'pierce'. Снимать при истечении буффа — через
//     одноразовый хук в expirationCallback (см. ниже).
//
// Реализация снятия pierce: при выдаче keyword'а пушим запись в
// state.pendingZeynPierceExpire = [{cardInstanceId, sourceId, expiresAtTurn}].
// Хук на onTurnStart проверяет state.currentTurn >= expiresAtTurn и снимает
// keyword. Это локальная инфра — для одной карты простая запись.

import { applyTemporaryBuff } from '../../buffs.js';
import { addPermanentKeyword } from '../../keywords/permanent.js';
import { removeKeyword, KW } from '../../keywords.js';
import { registerOnTurnStartExtraHandler } from '../deus/hooks.js';
import { HERO_ID } from './zeyn.js';
import { registerImplementedAbility } from './warn-unimplemented.js';

registerImplementedAbility(HERO_ID, 'active');

const LABEL = 'Зейн (active)';
const SOURCE_ID = 'liv_neyro_hirurg_zeyn_active';

export { HERO_ID };

export function getZeynActiveTargets(state, playerIdx) {
  const me = state?.players?.[playerIdx];
  if (!me) return [];
  return me.unitsOnField().filter(u => u.type === 'unit' && u.isAlive());
}

export function zeynActiveAvailable(state, playerIdx) {
  const p = state?.players?.[playerIdx];
  if (!p || p.hero?.id !== HERO_ID) return false;
  if (p.heroActiveUsed) return false;
  if (p.hero.hp <= 0) return false;
  if (getZeynActiveTargets(state, playerIdx).length === 0) return false;
  return true;
}

export function zeynActive(state, playerIdx, targetInstanceId = null) {
  if (!zeynActiveAvailable(state, playerIdx)) {
    return { ok: false, reason: 'unavailable' };
  }
  const me = state.players[playerIdx];
  const candidates = getZeynActiveTargets(state, playerIdx);
  let target = null;
  if (targetInstanceId != null) {
    target = candidates.find(u => u.instanceId === targetInstanceId) || null;
    if (!target) return { ok: false, reason: 'invalid_target' };
  } else {
    target = candidates[state.rng.int(candidates.length)];
  }
  if (!target) return { ok: false, reason: 'no_target' };
  const expiresAtTurn = state.currentTurn + 2;
  applyTemporaryBuff(state, target,
    { atk: 3, armor: 0, hp: 0 },
    expiresAtTurn,
    SOURCE_ID, LABEL
  );
  addPermanentKeyword(state, target, KW.PIERCE, SOURCE_ID, LABEL);
  // Регистрируем «pierce истекает в expiresAtTurn».
  if (!Array.isArray(state.pendingZeynPierceExpire)) state.pendingZeynPierceExpire = [];
  state.pendingZeynPierceExpire.push({
    cardInstanceId: target.instanceId,
    sourceId: SOURCE_ID,
    expiresAtTurn
  });
  state.log?.add(
    `🩺 ${LABEL} [${me.name}] → ${target.name_ru}: +3/0/0 + Пробитие до начала хода ${expiresAtTurn}`
  );
  me.heroActiveUsed = true;
  return { ok: true, target, expiresAtTurn };
}

// Снятие pierce'а в Ф1 хода expiresAtTurn — после стандартного снятия
// временных баффов. Идём по pending-списку, удаляем истёкшие записи и
// чистим keyword из persistentKeywords + card.keywords (если других
// источников 'pierce' нет — простая модель: pierce от Зейна — единственный
// источник у этой карты).
registerOnTurnStartExtraHandler((state) => {
  const pending = state.pendingZeynPierceExpire;
  if (!Array.isArray(pending) || pending.length === 0) return;
  const surviving = [];
  for (const item of pending) {
    if (state.currentTurn < item.expiresAtTurn) {
      surviving.push(item);
      continue;
    }
    // Найти карту по instanceId среди обоих игроков (на поле/в руке/сбросе).
    const card = findCardByInstanceId(state, item.cardInstanceId);
    if (card) {
      // Снимаем pierce из persistentKeywords, потом — из card.keywords если
      // иных источников нет.
      if (Array.isArray(card.persistentKeywords)) {
        const idx = card.persistentKeywords.indexOf(KW.PIERCE);
        if (idx >= 0) card.persistentKeywords.splice(idx, 1);
      }
      // Если pierce больше нигде не висит (derived, base) — снять.
      const stillSourced =
        (card.template?.keywords || []).includes(KW.PIERCE) ||
        (Array.isArray(card.persistentKeywords) && card.persistentKeywords.includes(KW.PIERCE)) ||
        (Array.isArray(card.derivedKeywords)    && card.derivedKeywords.includes(KW.PIERCE));
      if (!stillSourced) removeKeyword(card, KW.PIERCE);
      state.log?.add(`⏳ ${item.sourceId}: Пробитие снято с ${card.name_ru}`);
    }
  }
  state.pendingZeynPierceExpire = surviving;
});

function findCardByInstanceId(state, id) {
  if (id == null) return null;
  for (const p of state.players) {
    for (const u of p.unitsOnField()) if (u.instanceId === id) return u;
    for (const c of p.hand)            if (c.instanceId === id) return c;
    for (const c of p.discard)         if (c.instanceId === id) return c;
  }
  return null;
}
